'use client'

import React from 'react'
import { Skeleton } from '@mantine/core'
import { RiArrowRightUpLine } from '@remixicon/react'
import { useSession } from 'next-auth/react'
import { UserAvatar } from '../../../shared/UserAvatar'
import { useProfileMe } from '../../../hooks/useProfileMe'

export interface SidebarProfileProps {
  /** Where "View account" goes. Defaults to the shared global portal. */
  accountHref?: string
  accountLabel?: string
}

/**
 * `profiles/me/` answers with an `{ error }` envelope instead of throwing on
 * some failures, so an unchecked response looks like a profile whose every
 * field is undefined.
 */
const isProfileErrorResponse = (value: unknown) =>
  Boolean(value && typeof value === 'object' && 'error' in value)

/**
 * The signed-in user, under the sidebar logo.
 *
 * Falls back through the session when the profile request fails or carries no
 * picture — the session already knows who is signed in, so the sidebar should
 * still be able to name them.
 */
export const SidebarProfile: React.FC<SidebarProfileProps> = ({
  accountHref = '/global-portal',
  accountLabel = 'View account',
}) => {
  const { data: profileResponse, isLoading } = useProfileMe()
  const { data: session } = useSession()

  const profile = isProfileErrorResponse(profileResponse) ? null : (profileResponse as any)
  const sessionUser = session?.user as any
  const sessionProfile = sessionUser?.user_profile

  const email =
    profile?.primary_email ||
    sessionProfile?.email ||
    sessionUser?.email ||
    sessionUser?.user_data?.email ||
    ''
  const firstName = profile?.first_name || sessionProfile?.first_name
  const lastName = profile?.last_name || sessionProfile?.last_name
  const userName =
    profile?.display_name ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    sessionUser?.name ||
    email
  const userAvatar =
    profile?.profile_picture_url ||
    sessionProfile?.profile_picture_url ||
    sessionProfile?.profile_image ||
    sessionUser?.image ||
    null

  return (
    // Height follows the content: a fixed block left a gap under the account
    // link before the navigation started.
    //
    // The avatar is 48px rather than 64px. This block sits above the whole
    // navigation, and on a dashboard with eleven destinations every pixel it
    // takes is one the menu loses to a scrollbar.
    <div className="relative w-full flex flex-col items-center pb-1">
      <UserAvatar imageSrc={userAvatar} name={userName} width={48} height={48} unoptimized />
      <div className="mt-1.5 flex flex-col items-center gap-0.5">
        {isLoading && !userName ? (
          <Skeleton height={10} width="50%" />
        ) : (
          userName && <div className="text-[#312345] text-[13px] font-semibold">{userName}</div>
        )}
        <a
          href={accountHref}
          className="dashboard-nav-link flex cursor-pointer items-center gap-0.5 hover:opacity-70 transition-opacity"
        >
          <span className="text-[#475467] text-xs font-medium leading-tight">{accountLabel}</span>
          <RiArrowRightUpLine size={16} color="#9CA3AF" />
        </a>
      </div>
    </div>
  )
}
